import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortByAdvanced } from 'webapp/utils/metric-sort';

import eq from 'webapp/helpers/eq';
import makeArray from 'webapp/helpers/make-array';
import CommonSecurityInfoComponent from 'webapp/components/common/security-info';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricLibraryGridItemComponent, { Size } from 'webapp/components/metric-library/grid-item';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SourceTypeInfoTabsAboutComponent from 'webapp/components/source-type-info-tabs/about';
import SourceTypeInfoTabsHelpArticlesComponent from 'webapp/components/source-type-info-tabs/help-articles';
import SourceConnectButtonComponent from 'webapp/components/source/connect/button';
import ToolTipComponent from 'webapp/components/tool-tip';

import type { Metric } from 'webapp/models-custom';

const enum Tab {
	ABOUT = 'about',
	BASIC_METRICS = 'basic-metrics',
	CUSTOM_METRICS = 'custom-metrics',
	CALCULATED_METRICS = 'calculated-metrics',
	HELP = 'help-articles',
}

interface SourceTypeTab {
	id: Tab;
	text: string;
	itemsCount?: number;
	isShown: boolean;
}

export default class SourceTypeInfoPopupComponent extends PopupInnerModalComponent<PopupData.Source.SourceTypeInfo> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('connect') declare connectService: Service.Connect;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked selectedTab: Tab | '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.INTEGRATION_POPUP_OPENED, {
			type: this.model.key,
		});

		// To select basic-metrics if exists, otherwise first shown tab
		let initialSelectedTab = this.tabs.find((item) => item.id === Tab.BASIC_METRICS);

		if (!initialSelectedTab?.isShown) {
			initialSelectedTab = this.tabs.find((item) => item.isShown);
		}

		this.selectedTab = initialSelectedTab?.id ?? '';

		this.model.requestExtraInfo();
	}

	get model() {
		return this.popupData.model;
	}

	get connectButtonText() {
		return this.model.isWithCustomToken ? 'New token' : undefined; // for default
	}

	get connectButtonIcon(): IconName | '' | undefined {
		return this.model.isWithCustomToken ? '' : undefined; // for default
	}

	get isContentShown() {
		return this.selectedTab || this.showConnectNotes;
	}

	get showHelp() {
		return !this.accountService.hideHelpArticles;
	}

	get showConnectNotes() {
		return this.showHelp && this.model.connectNotesFormatted && !this.isAbout;
	}

	get sourceTypeMetrics() {
		return this.mmMetricsService.getGenericMetricsForSourceTypeKey(this.model.key);
	}

	@cached
	get normalMetrics() {
		const metrics = this.sourceTypeMetrics.filter((m) => m.isTypeBasic);
		return sortByAdvanced(metrics);
	}

	@cached
	get queryMetrics() {
		const metrics = this.sourceTypeMetrics.filter((m) => m.isTypeCustomQueryOrGeneric);
		return sortByAdvanced(metrics);
	}

	@cached
	get calculatedMetrics() {
		const metrics = this.sourceTypeMetrics.filter((m) => m.isTypeMetricCalculationOrGeneric);
		return sortByAdvanced(metrics);
	}

	get tabMetrics() {
		if (this.isBasicMetrics) {
			return this.normalMetrics as Metric[];
		} else if (this.isCustomMetrics) {
			return this.queryMetrics as Metric[];
		} else if (this.isCalculatedMetrics) {
			return this.calculatedMetrics as Metric[];
		} else {
			return [];
		}
	}

	@cached
	get tabs() {
		const tabs: SourceTypeTab[] = [],
			normalMetricsLength = this.normalMetrics.length,
			queryMetricsLength = this.queryMetrics.length,
			calculatedMetricsLength = this.calculatedMetrics.length;

		if (this.model) {
			tabs.push(
				{
					id: Tab.ABOUT,
					text: 'About',
					isShown: !!this.model.extraInfo?.content && this.showHelp,
				},
				{
					id: Tab.BASIC_METRICS,
					text: 'Prebuilt metrics',
					itemsCount: normalMetricsLength,
					isShown: normalMetricsLength > 0,
				},
				{
					id: Tab.CUSTOM_METRICS,
					text: 'Built metrics',
					itemsCount: queryMetricsLength,
					isShown: queryMetricsLength > 0,
				},
				{
					id: Tab.CALCULATED_METRICS,
					text: 'Calculated metrics',
					itemsCount: calculatedMetricsLength,
					isShown: calculatedMetricsLength > 0,
				},
				{
					id: Tab.HELP,
					text: 'Help articles',
					isShown: !!(this.model.uniqueArticleUrls || []).length && this.showHelp,
				},
			);
		}

		return tabs;
	}

	get isAbout() {
		return this.selectedTab === Tab.ABOUT;
	}

	get isBasicMetrics() {
		return this.selectedTab === Tab.BASIC_METRICS;
	}

	get isCustomMetrics() {
		return this.selectedTab === Tab.CUSTOM_METRICS;
	}

	get isCalculatedMetrics() {
		return this.selectedTab === Tab.CALCULATED_METRICS;
	}

	get isHelpArticles() {
		return this.selectedTab === Tab.HELP;
	}

	get itemSize() {
		return Size.SMALL;
	}

	@action
	openTab(tab: Tab) {
		this.selectedTab = tab;
	}

	@action
	connect() {
		this.popupData.onConnect(this.model);
	}

	@action
	helpConnect() {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		if (this.currentUserService.model?.isVerified) {
			this.model.requestConnect();
		} else {
			this.connectService.openEmailVerificationInfoPopup();
		}
	}
	<template>
		<div class="header title">
			<div class="left">
				<div
					class="avatar-wrapper
						{{if this.model.shownBadges 'has-badge'}}
						{{if this.model.qbSupportText 'has-qb-plus'}}"
				>
					<img class="avatar avatar--big" src={{this.model.avatarBig}} width="185" alt />
					<img class="avatar avatar--small" src={{this.model.avatar}} width="40" alt />

					{{#if this.model.shownBadges}}
						{{#each this.model.shownBadges as |badge|}}
							<span class={{badge}}>{{badge}}</span>
						{{/each}}
					{{/if}}

					{{#if this.model.qbSupportText}}
						<span class="qb-plus">
							<CommonUiIconComponent @name="Data" @size="xs" />
							<ToolTipComponent
								@text={{this.model.qbSupportText}}
								@theme="light"
								@interactive={{true}}
							/>
						</span>
					{{/if}}
				</div>

				<div class="source-description">
					<h3>{{this.model.name}}</h3>
					<p>{{this.model.description}}</p>
				</div>
			</div>
			<div class="right">
				<SourceConnectButtonComponent
					data-testid="test-connectPopupConnectButton"
					@sourceType={{this.model}}
					@onConnect={{this.connect}}
					@text={{this.connectButtonText}}
					@icon={{this.connectButtonIcon}}
					@extraClass="connect-now next"
					@size="big"
					@noDelay={{true}}
					@theme="green"
				/>
				{{#if this.model.showConnectHelp}}
					<a
						role="button"
						class="connect-help button size-big theme-blue-outlined
							{{unless this.currentUserService.model.isVerified 'disabled'}}"
						{{on "click" this.helpConnect}}
					>
						<CommonUiIconComponent @name="Mail" @size="sm" />Ask teammate to connect
					</a>
				{{/if}}
			</div>
		</div>

		{{#if this.tabs}}
			<ul class="modalTabs modalTabs-2">
				{{#each this.tabs as |tab|}}
					{{#if tab.isShown}}
						<li
							role="button"
							class="navigation-button
								{{if (eq this.selectedTab tab.id) 'navigation-button--selected'}}"
							{{on "click" (fn this.openTab tab.id)}}
						>
							{{! template-lint-disable link-href-attributes }}
							<a>{{tab.text}}</a>

							{{#if tab.itemsCount}}
								<small>{{tab.itemsCount}}</small>
							{{/if}}
						</li>
					{{/if}}
				{{/each}}
			</ul>
		{{/if}}

		{{#if this.isContentShown}}
			<div class="content">

				<div class="content-wrapper">
					{{#if this.showConnectNotes}}
						{{{this.model.connectNotesFormatted}}}
					{{/if}}

					{{#if this.isAbout}}
						<SourceTypeInfoTabsAboutComponent @model={{this.model}} />
					{{else if this.isHelpArticles}}
						<SourceTypeInfoTabsHelpArticlesComponent @model={{this.model}} />
					{{else}}
						<div>
							<ul
								class="flex-ct flex-ct--size-block-metric flex-ct--size-block-metric-condensed"
							>
								{{#each this.tabMetrics as |block|}}
									<MetricLibraryGridItemComponent
										@model={{block}}
										@size={{this.itemSize}}
										@hideActions={{true}}
										@isVisibleOnScreen={{true}}
									/>
								{{/each}}

								{{#each (makeArray length=5)}}
									<li class="flex-item flex-item--fake"></li>
								{{/each}}
							</ul>
						</div>
					{{/if}}
				</div>

				<div class="security-info-wrapper">
					<CommonSecurityInfoComponent />
				</div>

			</div>
		{{/if}}
	</template>
}
